import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';

// Layouts
import Layout from '@/components/Layout';
import SupplierLayout from '@/components/SupplierLayout';
import InventoryLayout from '@/components/InventoryLayout';
import ProductionLayout from '@/components/ProductionLayout';
import QualityLayout from '@/components/QualityLayout';
import SubcontractLayout from '@/components/SubcontractLayout';

// Auth Pages
import LoginPage from '@/pages/LoginPage';

// ERP Home
import ERPHomePage from '@/pages/ERPHomePage';
import NotificationsPage from '@/pages/NotificationsPage';
import MyProfilePage from '@/pages/MyProfilePage';

// Calibration Module
import DashboardPage from '@/pages/DashboardPage';
import ToolsPage from '@/pages/ToolsPage';
import ToolDetailPage from '@/pages/ToolDetailPage';
import AuditLogPage from '@/pages/AuditLogPage';
import CalibrationReportPage from '@/pages/CalibrationReportPage';

// Supplier Module
import SupplierDashboardPage from '@/pages/SupplierDashboardPage';
import SuppliersPage from '@/pages/SuppliersPage';
import SupplierFormPage from '@/pages/SupplierFormPage';
import SupplierDetailPage from '@/pages/SupplierDetailPage';
import SupplierReevaluationPage from '@/pages/SupplierReevaluationPage';
import SupplierImportPage from '@/pages/supplier/SupplierImportPage';
import SupplierReportPage from '@/pages/supplier/SupplierReportPage';
import SupplierPerformanceReportPage from '@/pages/supplier/SupplierPerformanceReportPage';

// Inventory & Production Module
import InventoryDashboard from '@/pages/inventory/InventoryDashboard';
import ItemMasterPage from '@/pages/inventory/ItemMasterPage';
import ItemFormPage from '@/pages/inventory/ItemFormPage';
import ItemDetailPage from '@/pages/inventory/ItemDetailPage';
import GodownMasterPage from '@/pages/inventory/GodownMasterPage';
import TransactionListPage from '@/pages/inventory/TransactionListPage';
import TransactionFormPage from '@/pages/inventory/TransactionFormPage';
import StockViewPage from '@/pages/inventory/StockViewPage';
import LowStockReportPage from '@/pages/inventory/LowStockReportPage';
import StockAgingReportPage from '@/pages/inventory/StockAgingReportPage';
import StockManagementPage from '@/pages/inventory/StockManagementPage';
import JobWorkDashboard from '@/pages/inventory/JobWorkDashboard';
import CategoriesPage from '@/pages/inventory/CategoriesPage';
import PendingApprovalsPage from '@/pages/inventory/PendingApprovalsPage';
import TraceabilityPage from '@/pages/inventory/TraceabilityPage';
import TraceReportPage from '@/pages/inventory/TraceReportPage';

// Production Module
import ProductionDashboard from '@/pages/production/ProductionDashboard';
import WorkCentersPage from '@/pages/inventory/WorkCentersPage';
import RoutesPage from '@/pages/inventory/RoutesPage';
import RouteFormPage from '@/pages/inventory/RouteFormPage';
import WorkOrdersPage from '@/pages/inventory/WorkOrdersPage';
import ScrapDisposalPage from '@/pages/inventory/ScrapDisposalPage';
import SubcontractPage from '@/pages/inventory/SubcontractPage';
import TraceabilityReportPage from '@/pages/subcontract/TraceabilityReportPage';
import SubcontractDashboard from '@/pages/subcontract/SubcontractDashboard';
import QualityCheckPage from '@/pages/production/QualityCheckPage';
import MRBPage from '@/pages/production/MRBPage';

// Quality Management Module
import QualityDashboard from '@/pages/quality/QualityDashboard';
import RejectionPPMPage from '@/pages/quality/RejectionPPMPage';
import CustomerComplaintsPage from '@/pages/quality/CustomerComplaintsPage';
import ComplaintDetailPage from '@/pages/quality/ComplaintDetailPage';
import InternalNCRPage from '@/pages/quality/InternalNCRPage';
import InternalNCRDetailPage from '@/pages/quality/InternalNCRDetailPage';
import RejectionReasonsPage from '@/pages/quality/RejectionReasonsPage';
import CustomerScorecardPage from '@/pages/quality/CustomerScorecardPage';

// Contract Management Module
import ContractLayout from '@/components/ContractLayout';
import ContractDashboard from '@/pages/contracts/ContractDashboard';
import CustomerMasterPage from '@/pages/contracts/CustomerMasterPage';
import CustomerFormPage from '@/pages/contracts/CustomerFormPage';
import CustomerDetailPage from '@/pages/contracts/CustomerDetailPage';
import PurchaseOrdersPage from '@/pages/contracts/PurchaseOrdersPage';
import PurchaseOrderFormPage from '@/pages/contracts/PurchaseOrderFormPage';
import OtdReportPage from '@/pages/contracts/OtdReportPage';
import HRLayout from '@/components/HRLayout';
import HREmployeesPage from '@/pages/hr/EmployeesPage';
import SimpleMasterPage from '@/pages/hr/SimpleMasterPage';
import HRQualificationsPage from '@/pages/hr/QualificationsPage';
import HRSkillsPage from '@/pages/hr/SkillsPage';
import HRFunctionalPositionsPage from '@/pages/hr/FunctionalPositionsPage';
import HRSkillLevelsPage from '@/pages/hr/SkillLevelsPage';
import HREmployeeDetailPage from '@/pages/hr/EmployeeDetailPage';
import HRReportingTreePage from '@/pages/hr/ReportingTreePage';
import HRTrainingsPage from '@/pages/hr/TrainingsPage';
import HRTrainingSessionsPage from '@/pages/hr/TrainingSessionsPage';
import HRPendingSkillUpdatesPage from '@/pages/hr/PendingSkillUpdatesPage';
import HRPendingEvaluationsPage from '@/pages/hr/PendingEvaluationsPage';
import HRCompletedEvaluationsPage from '@/pages/hr/CompletedEvaluationsPage';
import HRCompetencyTestsPage from '@/pages/hr/CompetencyTestsPage';
import HRTakeTestPage from '@/pages/hr/TakeTestPage';
import PublicTestPage from '@/pages/hr/PublicTestPage';
import RecruitmentPage from '@/pages/hr/RecruitmentPage';
import CareersPage from '@/pages/CareersPage';
import HRTrainingPlanPage from '@/pages/hr/TrainingPlanPage';
import HRReportsPage from '@/pages/hr/HRReportsPage';
import PurchaseOrderDetailPage from '@/pages/contracts/PurchaseOrderDetailPage';

// Dispatch Module
import DispatchLayout from '@/layouts/DispatchLayout';
import DispatchPage from '@/pages/dispatch/DispatchPage';
import DispatchPlanReportPage from '@/pages/dispatch/DispatchPlanReportPage';

// Purchase Module
import PurchaseLayout from '@/components/PurchaseLayout';
import PurchaseDashboard from '@/pages/purchase/PurchaseDashboard';
import IndentPage from '@/pages/purchase/IndentPage';
import PurchaseRequestsPage from '@/pages/purchase/PurchaseRequestsPage';
import CreatePRPage from '@/pages/purchase/CreatePRPage';
import SpendAnalyticsPage from '@/pages/purchase/SpendAnalyticsPage';

// User Management
import UsersPage from '@/pages/UsersPage';
import LoginAuditPage from '@/pages/LoginAuditPage';

// Admin Tools
import AdminToolsPage from '@/pages/admin/AdminToolsPage';
import EmailSettingsPage from '@/pages/admin/EmailSettingsPage';

// Program Management Module
import ProgramLayout from '@/components/ProgramLayout';
import ProgramDashboard from '@/pages/programs/ProgramDashboard';
import ProgramListPage from '@/pages/programs/ProgramListPage';
import ProgramFormPage from '@/pages/programs/ProgramFormPage';
import ProgramDetailPage from '@/pages/programs/ProgramDetailPage';
import ProgramAnalyticsPage from '@/pages/programs/ProgramAnalyticsPage';
import ProgramFinancialsPage from '@/pages/programs/ProgramFinancialsPage';
import MyTasksPage from '@/pages/programs/MyTasksPage';
import TaskDashboardPage from '@/pages/programs/TaskDashboardPage';
import SalesSpendReportPage from '@/pages/reports/SalesSpendReportPage';

// Help
import HelpPage from '@/pages/help/HelpPage';

function ProtectedRoute({ children, adminOnly, appAccess }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  if (appAccess && user.role !== 'admin') {
    const needed = Array.isArray(appAccess) ? appAccess : [appAccess];
    const ok = needed.some((a) => (user.app_roles?.[a] || 'none') !== 'none');
    if (!ok) return <Navigate to="/" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/test/:token" element={<PublicTestPage />} />
      <Route path="/careers" element={<CareersPage />} />
      
      {/* Help - Accessible to all logged-in users */}
      <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      
      {/* ERP Home - No sidebar layout */}
      <Route path="/" element={<ProtectedRoute><ERPHomePage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><MyProfilePage /></ProtectedRoute>} />
      
      {/* Calibration Module - Redirect to Quality Management */}
      <Route path="/calibration" element={<Navigate to="/quality" replace />} />
      <Route path="/calibration/*" element={<Navigate to="/quality" replace />} />
      
      {/* Supplier Module - With sidebar layout */}
      <Route path="/suppliers" element={<ProtectedRoute appAccess="supplier_mgmt"><SupplierLayout /></ProtectedRoute>}>
        <Route index element={<SupplierDashboardPage />} />
        <Route path="list" element={<SuppliersPage />} />
        <Route path="report" element={<SupplierReportPage />} />
        <Route path="performance-report" element={<SupplierPerformanceReportPage />} />
      </Route>
      
      {/* Supplier standalone pages (no sidebar - full-width) */}
      <Route path="/suppliers/new" element={<ProtectedRoute appAccess="supplier_mgmt"><SupplierFormPage /></ProtectedRoute>} />
      <Route path="/suppliers/import" element={<ProtectedRoute adminOnly><SupplierImportPage /></ProtectedRoute>} />
      <Route path="/suppliers/:id" element={<ProtectedRoute appAccess="supplier_mgmt"><SupplierDetailPage /></ProtectedRoute>} />
      <Route path="/suppliers/:id/edit" element={<ProtectedRoute appAccess="supplier_mgmt"><SupplierFormPage /></ProtectedRoute>} />
      <Route path="/suppliers/:id/reevaluate" element={<ProtectedRoute appAccess="supplier_mgmt"><SupplierReevaluationPage /></ProtectedRoute>} />
      
      {/* Inventory Module */}
      <Route path="/inventory" element={<ProtectedRoute appAccess="inventory_mgmt"><InventoryLayout /></ProtectedRoute>}>
        <Route index element={<InventoryDashboard />} />
        <Route path="items" element={<ItemMasterPage />} />
        <Route path="godowns" element={<GodownMasterPage />} />
        <Route path="transactions" element={<TransactionListPage />} />
        <Route path="stock" element={<StockViewPage />} />
        <Route path="stock-management" element={<StockManagementPage />} />
        <Route path="low-stock" element={<LowStockReportPage />} />
        <Route path="stock-aging" element={<StockAgingReportPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="pending-approvals" element={<PendingApprovalsPage />} />
        <Route path="traceability" element={<TraceabilityPage />} />
        <Route path="trace-report" element={<TraceReportPage />} />
      </Route>
      
      {/* Inventory standalone pages */}
      <Route path="/inventory/items/new" element={<ProtectedRoute appAccess="inventory_mgmt"><ItemFormPage /></ProtectedRoute>} />
      <Route path="/inventory/items/:id" element={<ProtectedRoute appAccess="inventory_mgmt"><ItemDetailPage /></ProtectedRoute>} />
      <Route path="/inventory/items/:id/edit" element={<ProtectedRoute appAccess="inventory_mgmt"><ItemFormPage /></ProtectedRoute>} />
      <Route path="/inventory/transactions/new" element={<ProtectedRoute appAccess="inventory_mgmt"><TransactionFormPage /></ProtectedRoute>} />

      {/* Production Module */}
      <Route path="/production" element={<ProtectedRoute appAccess="inventory_mgmt"><ProductionLayout /></ProtectedRoute>}>
        <Route index element={<ProductionDashboard />} />
        <Route path="work-centers" element={<WorkCentersPage />} />
        <Route path="routes" element={<RoutesPage />} />
        <Route path="work-orders" element={<WorkOrdersPage />} />
        <Route path="scrap" element={<ScrapDisposalPage />} />
      </Route>
      
      {/* Production standalone pages */}
      <Route path="/production/routes/new" element={<ProtectedRoute appAccess="inventory_mgmt"><RouteFormPage /></ProtectedRoute>} />
      <Route path="/production/routes/:id" element={<ProtectedRoute appAccess="inventory_mgmt"><RouteFormPage /></ProtectedRoute>} />

      {/* Subcontracting Module */}
      <Route path="/subcontract" element={<ProtectedRoute appAccess="inventory_mgmt"><SubcontractLayout /></ProtectedRoute>}>
        <Route index element={<SubcontractDashboard />} />
        <Route path="challans" element={<SubcontractPage />} />
        <Route path="job-tracker" element={<JobWorkDashboard />} />
        <Route path="traceability-report" element={<TraceabilityReportPage />} />
      </Route>

      {/* Quality Management Module */}
      <Route path="/quality" element={<ProtectedRoute appAccess="inventory_mgmt"><QualityLayout /></ProtectedRoute>}>
        <Route index element={<QualityDashboard />} />
        <Route path="incoming-qc" element={<QualityCheckPage />} />
        <Route path="mrb" element={<MRBPage />} />
        <Route path="rejection-ppm" element={<RejectionPPMPage />} />
        <Route path="complaints" element={<CustomerComplaintsPage />} />
        <Route path="complaints/:id" element={<ComplaintDetailPage />} />
        <Route path="internal-ncr" element={<InternalNCRPage />} />
        <Route path="internal-ncr/:id" element={<InternalNCRDetailPage />} />
        <Route path="rejection-reasons" element={<RejectionReasonsPage />} />
        <Route path="customer-scorecard" element={<CustomerScorecardPage />} />
        <Route path="calibration" element={<ToolsPage />} />
        <Route path="calibration/:id" element={<ToolDetailPage />} />
        <Route path="calibration-report" element={<CalibrationReportPage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
      </Route>

      {/* Contract Management Module - With sidebar layout */}
      <Route path="/contracts" element={<ProtectedRoute appAccess="contract_mgmt"><ContractLayout /></ProtectedRoute>}>
        <Route index element={<ContractDashboard />} />
        <Route path="customers" element={<CustomerMasterPage />} />
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="otd-report" element={<OtdReportPage />} />
      </Route>
      
      {/* Contract standalone pages */}
      <Route path="/contracts/customers/new" element={<ProtectedRoute appAccess="contract_mgmt"><CustomerFormPage /></ProtectedRoute>} />
      <Route path="/contracts/customers/:id" element={<ProtectedRoute appAccess="contract_mgmt"><CustomerDetailPage /></ProtectedRoute>} />
      <Route path="/contracts/customers/:id/edit" element={<ProtectedRoute appAccess="contract_mgmt"><CustomerFormPage /></ProtectedRoute>} />
      <Route path="/contracts/purchase-orders/new" element={<ProtectedRoute appAccess="contract_mgmt"><PurchaseOrderFormPage /></ProtectedRoute>} />
      <Route path="/contracts/purchase-orders/:id" element={<ProtectedRoute appAccess="contract_mgmt"><PurchaseOrderDetailPage /></ProtectedRoute>} />

      {/* HR & Training Module */}
      <Route path="/hr" element={<ProtectedRoute appAccess={["hr_training", "recruitment"]}><HRLayout /></ProtectedRoute>}>
        <Route index element={<HREmployeesPage />} />
        <Route path="employees/:id" element={<HREmployeeDetailPage />} />
        <Route path="reporting-tree" element={<HRReportingTreePage />} />
        <Route path="skill-groups" element={<SimpleMasterPage kind="skill-groups" title="Skill Groups" singular="Skill Group" />} />
        <Route path="skills" element={<HRSkillsPage />} />
        <Route path="functions" element={<SimpleMasterPage kind="functions" title="Functions" singular="Function" seed />} />
        <Route path="positions" element={<SimpleMasterPage kind="positions" title="Positions" singular="Position" />} />
        <Route path="qualifications" element={<HRQualificationsPage />} />
        <Route path="functional-positions" element={<HRFunctionalPositionsPage />} />
        <Route path="trainings" element={<HRTrainingsPage />} />
        <Route path="training-sessions" element={<HRTrainingSessionsPage />} />
        <Route path="training-plan" element={<HRTrainingPlanPage />} />
        <Route path="pending-skill-updates" element={<HRPendingSkillUpdatesPage />} />
        <Route path="pending-evaluations" element={<HRPendingEvaluationsPage />} />
        <Route path="completed-evaluations" element={<HRCompletedEvaluationsPage />} />
        <Route path="competency-tests" element={<HRCompetencyTestsPage />} />
        <Route path="recruitment" element={<RecruitmentPage />} />
        <Route path="tests/:id/take" element={<HRTakeTestPage />} />
        <Route path="reports" element={<HRReportsPage />} />
        <Route path="skill-levels" element={<HRSkillLevelsPage />} />
      </Route>

      {/* Dispatch Module */}
      <Route path="/dispatch" element={<ProtectedRoute appAccess="dispatch_mgmt"><DispatchLayout /></ProtectedRoute>}>
        <Route index element={<DispatchPage />} />
        <Route path="reports" element={<DispatchPlanReportPage />} />
      </Route>

      {/* Purchase Module */}
      <Route path="/purchase" element={<ProtectedRoute appAccess="supplier_mgmt"><PurchaseLayout /></ProtectedRoute>}>
        <Route index element={<PurchaseDashboard />} />
        <Route path="indent" element={<IndentPage />} />
        <Route path="requests" element={<PurchaseRequestsPage />} />
        <Route path="requests/new" element={<CreatePRPage />} />
        <Route path="analytics" element={<SpendAnalyticsPage />} />
      </Route>

      {/* User Management - Standalone page */}
      <Route path="/users" element={<ProtectedRoute adminOnly><UsersPage /></ProtectedRoute>} />
      <Route path="/login-audit" element={<ProtectedRoute adminOnly><LoginAuditPage /></ProtectedRoute>} />
      
      {/* Admin Tools */}
      <Route path="/admin/tools" element={<ProtectedRoute adminOnly><AdminToolsPage /></ProtectedRoute>} />
      <Route path="/admin/email-settings" element={<ProtectedRoute adminOnly><EmailSettingsPage /></ProtectedRoute>} />
      
      {/* Program Management Module */}
      <Route path="/programs" element={<ProtectedRoute appAccess="project_mgmt"><ProgramLayout /></ProtectedRoute>}>
        <Route index element={<ProgramDashboard />} />
        <Route path="list" element={<ProgramListPage />} />
        <Route path="new" element={<ProgramFormPage />} />
        <Route path="my-tasks" element={<MyTasksPage />} />
        <Route path="task-dashboard" element={<TaskDashboardPage />} />
        <Route path=":id" element={<ProgramDetailPage />} />
        <Route path=":id/edit" element={<ProgramFormPage />} />
        <Route path=":id/financials" element={<ProgramFinancialsPage />} />
        <Route path="analytics" element={<ProgramAnalyticsPage />} />
        <Route path="reports/sales-spend" element={<SalesSpendReportPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        {/* Global Toaster — guaranteed to be mounted for every route,
            including standalone form pages that are NOT wrapped in a
            layout (e.g. /contracts/purchase-orders/new, /contracts/
            customers/new, etc.). Per-layout Toasters used to be the
            source of "silent failures" on those pages — they fired
            toast.error but the host never rendered the container. */}
        <Toaster position="top-right" richColors closeButton duration={6000} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
